<template>
	<div class="t-modal-backdrop" @click.self="$emit('close')">
		<div class="t-modal">
			<div class="t-modal-head">
				<h3>{{ form.id ? "עריכת משימה #" + form.id : "משימה חדשה" }}</h3>
				<span class="t-close" @click="$emit('close')">×</span>
			</div>

			<div class="t-modal-body">
				<div class="t-row">
					<div class="t-col">
						<label>כותרת</label>
						<input v-model="form.title" placeholder="מה צריך לעשות?" />
					</div>
				</div>

				<div class="t-row">
					<div class="t-col">
						<label>תיאור / פירוט</label>
						<textarea v-model="form.description" rows="4" placeholder="פירוט המשימה..."></textarea>
					</div>
				</div>

				<div class="t-row">
					<div class="t-col">
						<label>פרויקט</label>
						<select v-model="form.project_id" :disabled="lockProject">
							<option :value="0">בחר פרויקט...</option>
							<option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
						</select>
					</div>
					<div class="t-col">
						<label>אחראי</label>
						<select v-model="form.assignee_id">
							<option :value="0">לא משוייך</option>
							<option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
						</select>
					</div>
				</div>

				<div class="t-row">
					<div class="t-col">
						<label>סטטוס</label>
						<select v-model="form.status">
							<option value="todo">להתחלה</option>
							<option value="in_progress">בעבודה</option>
							<option value="review">בבדיקה</option>
							<option value="done">הושלם</option>
							<option value="cancelled">בוטל</option>
						</select>
					</div>
					<div class="t-col">
						<label>עדיפות</label>
						<select v-model="form.priority">
							<option value="low">נמוכה</option>
							<option value="medium">בינונית</option>
							<option value="high">גבוהה</option>
							<option value="urgent">דחוף</option>
						</select>
					</div>
					<div class="t-col">
						<label>דדליין</label>
						<input v-model="form.deadline" type="datetime-local" />
					</div>
				</div>

				<!-- Comments -->
				<template v-if="form.id">
					<div class="t-section-title">תגובות <span class="t-live"><span class="t-live-dot"></span> בזמן אמת</span></div>

					<div ref="commentsBox" class="t-comments">
						<div v-if="!comments.length" style="text-align:center; color:var(--t-text-3); padding: 20px;">אין תגובות עדיין — הוסף את הראשונה</div>
						<div v-for="c in comments" :key="c.id" class="t-comment" :class="{mine: c.user_id == user?.id}">
							<div class="t-avatar sm" :style="{background: c.user_color || '#6366f1'}">{{ initials(c.user_name) }}</div>
							<div class="t-comment-body">
								<div class="t-cm-head">
									<span class="t-cm-name">{{ c.user_name }}</span>
									<span class="t-cm-time">{{ formatDateTime(c.created_at) }}</span>
									<span v-if="c.user_id == user?.id" class="t-cm-time" style="cursor:pointer; margin-inline-start:auto; color:var(--t-danger);" @click="deleteComment(c.id)">מחק</span>
								</div>
								<div class="t-cm-text">{{ c.content }}</div>
							</div>
						</div>
					</div>

					<div class="t-comment-form">
						<textarea v-model="newComment" placeholder="כתוב תגובה..." @keydown.enter.exact.prevent="sendComment"></textarea>
						<button class="t-btn primary" @click="sendComment">שלח</button>
					</div>

					<div class="t-section-title">היסטוריה</div>
					<div class="t-activity-feed" style="max-height: 160px; overflow-y: auto;">
						<div v-for="a in activity" :key="a.id" class="t-act">
							<div class="t-avatar sm" :style="{background: a.user_color || '#6366f1'}">{{ initials(a.user_name) }}</div>
							<div class="t-act-text">
								<b>{{ a.user_name }}</b> — {{ actionText(a.action) }}
								<span v-if="a.details" style="color:var(--t-text-2);">({{ a.details }})</span>
								<div class="t-act-time">{{ formatDateTime(a.created_at) }}</div>
							</div>
						</div>
					</div>
				</template>
			</div>

			<div class="t-modal-foot">
				<button v-if="form.id" class="t-btn danger" @click="del">מחק</button>
				<button class="t-btn ghost" @click="$emit('close')">סגור</button>
				<button class="t-btn primary" @click="save">{{ form.id ? "שמור שינויים" : "צור משימה" }}</button>
			</div>
		</div>
	</div>
</template>

<script>
import { TasksAPI, TasksRealtime, initials, formatDateTime } from "./TasksApi";

export default {
	name: "TaskModal",
	props: {
		taskId: { type: [Number, String], default: 0 },
		user: Object,
		users: { type: Array, default: () => [] },
		projectId: { type: [Number, String], default: 0 },
		lockProject: { type: Boolean, default: false },
		projectsList: { type: Array, default: () => [] },
	},
	emits: ["close", "saved"],
	data() {
		return {
			form: this.emptyForm(),
			projects: [],
			comments: [],
			activity: [],
			newComment: "",
			subscription: null,
		};
	},
	async mounted() {
		// If parent supplied projects, skip the API call entirely
		if (this.projectsList && this.projectsList.length) {
			this.projects = this.projectsList;
		} else {
			TasksAPI.projects().then(r => { this.projects = (r && r.items) || []; });
		}

		if (this.taskId) {
			const subPromise = TasksRealtime.subscribe("tasks-task-" + this.taskId, {
				"comment.new":     (d) => this.onNewComment(d.comment),
				"comment.deleted": (d) => { this.comments = this.comments.filter(c => c.id != d.comment_id); },
			}).then(s => { this.subscription = s; });
			await Promise.all([this.loadTask(), subPromise]);
		} else if (this.projectId) {
			this.form.project_id = +this.projectId;
		}
	},
	beforeUnmount() {
		if (this.subscription) TasksRealtime.unsubscribe(this.subscription);
	},
	methods: {
		initials, formatDateTime,
		emptyForm() {
			return {
				id: 0,
				project_id: this.projectId ? +this.projectId : 0,
				title: "",
				description: "",
				status: "todo",
				priority: "medium",
				assignee_id: 0,
				deadline: "",
			};
		},
		async loadTask() {
			const res = await TasksAPI.taskGet(this.taskId);
			if (!res || !res.task) return;
			const t = res.task;
			this.form = {
				id: +t.id,
				project_id: +t.project_id,
				title: t.title || "",
				description: t.description || "",
				status: t.status,
				priority: t.priority,
				assignee_id: +t.assignee_id || 0,
				deadline: t.deadline ? t.deadline.replace(" ", "T").slice(0, 16) : "",
			};
			this.comments = res.comments || [];
			this.activity = res.activity || [];
			this.$nextTick(() => this.scrollComments());
		},
		onNewComment(c) {
			if (!c) return;
			if (this.comments.some(x => x.id == c.id)) return;
			this.comments.push(c);
			this.$nextTick(() => this.scrollComments());
		},
		scrollComments() {
			const el = this.$refs.commentsBox;
			if (el) el.scrollTop = el.scrollHeight;
		},
		async save() {
			if (!this.form.title.trim() || !this.form.project_id) {
				alert("יש למלא כותרת ולבחור פרויקט");
				return;
			}
			const res = await TasksAPI.taskSave(this.form);
			if (res && res.id) {
				this.$emit("saved", res.id);
				this.$emit("close");
			}
		},
		async del() {
			if (!confirm("למחוק את המשימה לצמיתות?")) return;
			await TasksAPI.taskDelete(this.form.id);
			this.$emit("saved");
			this.$emit("close");
		},
		async sendComment() {
			const txt = this.newComment.trim();
			if (!txt || !this.form.id) return;
			this.newComment = "";
			const res = await TasksAPI.commentAdd(this.form.id, txt);
			if (res && res.comment) this.onNewComment(res.comment);
		},
		async deleteComment(id) {
			if (!confirm("למחוק את התגובה?")) return;
			await TasksAPI.commentDelete(id);
			this.comments = this.comments.filter(c => c.id != id);
		},
		actionText(a) {
			return ({ created: "יצר את המשימה", status_changed: "שינה סטטוס", assigned: "שינה אחראי", commented: "הוסיף תגובה" }[a] || a);
		},
	},
};
</script>
